import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { round } from 'es-toolkit';
import { format } from 'date-fns';
import { tz } from '@date-fns/tz';
import { Box, Stack, Typography } from '@mui/material';
import { Chart as ChartJS, TimeScale, LinearScale, LineElement, Tooltip, Legend, PointElement } from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';

import { tempUnitAtom, timeZoneAtom } from '@/interfaces/utils';
import { getTemperatureText } from '@/components/common/utils';

import type { ChartData, ChartOptions } from 'chart.js';
import type { GraphData } from '@/@types/device';

interface DeviceDetailGraphProps {
  tempData: GraphData[];
  humidData: GraphData[];
}

const options: ChartOptions<'line'> = {
  plugins: {
    legend: {
      position: 'top',
    },
    tooltip: {
      callbacks: {},
    },
  },
  elements: {
    point: {
      pointStyle: false,
      hitRadius: 8,
    },
    line: {
      spanGaps: false,
      tension: 0.4,
      cubicInterpolationMode: 'monotone',
    },
  },
  scales: {
    x: {
      type: 'time',
      time: {
        unit: 'hour',
      },
      ticks: {},
    },
    temperature: {
      position: 'left',
      type: 'linear',
      title: {
        display: true,
        text: '',
        font: { weight: 'bold' },
      },
      ticks: {},
    },
    humidity: {
      display: false,
      position: 'right',
      type: 'linear',
      title: {
        display: true,
        font: { weight: 'bold' },
      },
      ticks: {
        callback: (value) => round(Number(value), 0) + '%',
      },
      grid: { display: false },
    },
  },
};

ChartJS.register(TimeScale, LinearScale, LineElement, Tooltip, Legend, PointElement);

export default function DeviceDetailGraph({ tempData, humidData }: DeviceDetailGraphProps) {
  const timeZone = useAtomValue(timeZoneAtom);
  const tempUnit = useAtomValue(tempUnitAtom);
  const { t, i18n } = useTranslation();

  options.plugins.tooltip.callbacks.title = (items) => {
    const item = items[0];
    const { x: time } = item.raw as GraphData;
    const titleFormat = i18n.language.startsWith('ko') ? 'yyyy.MM.dd a hh:mm' : 'MM/dd/yyyy hh:mm a';
    return format(time, titleFormat, { in: tz(timeZone) });
  };
  options.plugins.tooltip.callbacks.label = (ctx) => {
    const { label } = ctx.dataset;
    const { y } = ctx.parsed;

    if (label === t('Devices.label.temperature')) return `${label}: ${y}℃`;
    return `${label}: ${y}%`;
  };
  options.scales.x.ticks.callback = (value) => {
    const timeStr = format(value, 'HH:mm', { in: tz(timeZone) });
    if (timeStr === '00:00') return format(value, 'MM/dd', { in: tz(timeZone) });
    return timeStr;
  };
  options.scales.temperature.title.text = t('Devices.label.temperature');
  options.scales.temperature.ticks.callback = (value) => getTemperatureText(value, tempUnit, true);
  options.scales.humidity.title.text = t('Devices.label.humidity');

  const data: ChartData<'line', GraphData[]> = {
    datasets: [
      {
        label: t('Devices.label.temperature'),
        data: tempData,
        yAxisID: 'temperature',
        borderColor: '#98B8E8',
        backgroundColor: '#98B8E8',
      },
    ],
  };

  if (humidData.length > 0) {
    options.scales.humidity.display = true;
    data.datasets.push({
      label: t('Devices.label.humidity'),
      data: humidData,
      yAxisID: 'humidity',
      borderColor: '#FD3B3B',
      backgroundColor: '#FD3B3B',
    });
  } else {
    options.scales.humidity.display = false;
  }

  if (tempData.length === 0)
    return (
      <Stack justifyContent="center" alignItems="center" sx={{ aspectRatio: 2, bgcolor: 'white' }}>
        <Typography color="textDisabled">{t('Devices.message.noData')}</Typography>
      </Stack>
    );
  return (
    <Box sx={{ aspectRatio: 2, bgcolor: 'white' }}>
      <Line options={options} data={data} />
    </Box>
  );
}
